import React, { useEffect, useState, useCallback } from 'react';

const LOG_URL = '/api/v1/event-calendar/sync-log';
const PAGE_SIZE = 25;
const VENDORS = ['LOCALEVENTSAPI', 'ALLEVENTS', 'PREDICTHQ', 'EVENTBRITE'];

const copyText = async (text) => {
  const s = String(text ?? '');
  try {
    if (navigator?.clipboard?.writeText) { await navigator.clipboard.writeText(s); return true; }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = s; ta.setAttribute('readonly', '');
    ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
};

function EventSyncLog({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [vendorFilter, setVendorFilter] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [modalRow, setModalRow] = useState(null);
  const [modalJson, setModalJson] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || 'GLOBAL',
  }), [token, selectedProperty]);

  const load = useCallback(() => {
    if (!selectedProperty?.chain_id) { setRows([]); setTotal(0); return; }
    setLoading(true); setError('');
    const offset = (page - 1) * PAGE_SIZE;
    const q = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (vendorFilter !== 'ALL') q.set('vendor', vendorFilter);
    fetch(`${LOG_URL}?${q.toString()}`, { headers: headers() })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (ok && data) {
          setRows(Array.isArray(data.rows) ? data.rows : []);
          setTotal(Number(data.total) || 0);
        } else {
          setError(data?.error || 'Failed to load log');
        }
      })
      .catch(() => setError('Network error while loading log'))
      .finally(() => setLoading(false));
  }, [headers, page, vendorFilter, selectedProperty]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [vendorFilter, selectedProperty?.chain_id]);

  const viewJson = (row) => {
    setModalRow(row); setModalJson(''); setModalLoading(true); setCopied(false);
    fetch(`${LOG_URL}/${row.id}`, { headers: headers() })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (ok && data) setModalJson(JSON.stringify(data.raw_response ?? {}, null, 2));
        else setModalJson(`// Failed to load: ${data?.error || 'unknown error'}`);
      })
      .catch(() => setModalJson('// Network error while loading JSON'))
      .finally(() => setModalLoading(false));
  };

  const closeModal = () => { setModalRow(null); setModalJson(''); };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (!selectedProperty?.chain_id) {
    return (
      <div className="page-shell">
        <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.6rem' }}>
          <span>Settings</span> <span style={{ color: '#cbd5e0' }}>›</span> <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>Events Log</span>
        </div>
        <h2 style={{ color: 'var(--theme-primary-dark)' }}>Events Log</h2>
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: '0.9rem 1.1rem', borderRadius: '6px', fontSize: '0.9rem' }}>
          Select an outlet in the top navigation to view its chain's event sync log.
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.6rem' }}>
        <span>Settings</span> <span style={{ color: '#cbd5e0' }}>›</span> <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>Events Log</span>
      </div>
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.25rem' }}>Events Log</h2>
      <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1.2rem' }}>
        Every attempt to fetch events from a configured Event Calendar vendor (LocalEventsAPI, AllEvents, PredictHQ, Eventbrite) — how many events came back, how many were new, and the raw response for each attempt.
      </p>

      {error && <div className="error-banner" style={{ marginBottom: '1rem' }}>{error}</div>}

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.9rem', flexWrap: 'wrap' }}>
        {[['ALL', 'All vendors'], ...VENDORS.map((v) => [v, v])].map(([k, l]) => (
          <button key={k} onClick={() => setVendorFilter(k)}
            style={{
              padding: '0.4rem 0.9rem', borderRadius: '999px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
              border: vendorFilter === k ? '1px solid var(--theme-primary)' : '1px solid #d1d5db',
              background: vendorFilter === k ? 'var(--theme-primary)' : '#fff',
              color: vendorFilter === k ? '#fff' : '#374151',
            }}>
            {l}
          </button>
        ))}
        <button onClick={load} disabled={loading} style={btnGhost}>{loading ? 'Refreshing…' : 'Refresh'}</button>
        <span style={{ marginLeft: 'auto', fontSize: '0.82rem', color: '#64748b' }}>{total} {total === 1 ? 'entry' : 'entries'}</span>
      </div>

      {rows.length === 0 ? (
        <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
          {loading ? 'Loading…' : 'No sync attempts logged yet for this chain.'}
        </p>
      ) : (
        <>
          <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border)', borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', fontSize: '0.85rem', minWidth: 760 }}>
              <thead>
                <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
                  <th style={logTh}>Date / Time</th>
                  <th style={logTh}>Vendor</th>
                  <th style={logTh}>Trigger</th>
                  <th style={logTh}>Cities</th>
                  <th style={{ ...logTh, textAlign: 'right' }}>Fetched</th>
                  <th style={{ ...logTh, textAlign: 'right' }}>New</th>
                  <th style={logTh}>Outcome</th>
                  <th style={logTh}>Detail</th>
                  <th style={logTh}>JSON</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const oc = String(r.outcome || '').toUpperCase();
                  const ocColor = oc === 'ACCEPTED' ? '#166534' : oc === 'ERROR' ? '#b91c1c' : '#b45309';
                  return (
                    <tr key={r.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                      <td style={{ ...logTd, whiteSpace: 'nowrap' }}>{new Date(r.created_at).toLocaleString()}</td>
                      <td style={{ ...logTd, fontWeight: 600 }}>{r.vendor}</td>
                      <td style={logTd}>{r.trigger_source === 'BULK_SYNC' ? 'Bulk sync' : 'Single vendor sync'}</td>
                      <td style={{ ...logTd, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.cities || ''}>{r.cities || '—'}</td>
                      <td style={{ ...logTd, textAlign: 'right' }}>{r.fetched_count ?? '—'}</td>
                      <td style={{ ...logTd, textAlign: 'right' }}>{r.inserted_count ?? '—'}</td>
                      <td style={{ ...logTd, fontWeight: 700, color: ocColor }}>{r.outcome || '—'}</td>
                      <td style={{ ...logTd, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#b91c1c' }} title={r.error_message || ''}>{r.error_message || ''}</td>
                      <td style={logTd}>
                        <button onClick={() => viewJson(r)} style={btnGhost}>View JSON</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginTop: '1rem' }}>
            <span style={{ fontSize: '0.82rem', color: '#64748b' }}>Page {page} of {totalPages}</span>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} style={{ ...btnGhost, opacity: page === 1 ? 0.5 : 1 }}>‹ Prev</button>
              <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ ...btnGhost, opacity: page === totalPages ? 0.5 : 1 }}>Next ›</button>
            </div>
          </div>
        </>
      )}

      {modalRow && (
        <div onClick={closeModal} style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '2rem',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'var(--theme-bg, #fff)', borderRadius: 10, width: '100%', maxWidth: 860,
            maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.2rem', borderBottom: '1px solid var(--theme-border)' }}>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--theme-primary-dark)' }}>{modalRow.vendor} — {modalRow.cities || 'Multiple cities'}</div>
                <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{new Date(modalRow.created_at).toLocaleString()} · {modalRow.fetched_count ?? 0} events fetched</div>
              </div>
              <button onClick={closeModal} style={{ ...btnGhost, fontWeight: 700 }}>✕ Close</button>
            </div>
            <div style={{ padding: '1rem 1.2rem', overflow: 'auto', flex: 1 }}>
              {modalLoading ? (
                <p style={{ color: '#6b7280' }}>Loading…</p>
              ) : (
                <pre style={{
                  background: '#0f172a', color: '#e2e8f0', padding: '1rem', borderRadius: 8,
                  fontSize: '0.78rem', lineHeight: 1.5, overflow: 'auto', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>{modalJson}</pre>
              )}
            </div>
            <div style={{ padding: '0.8rem 1.2rem', borderTop: '1px solid var(--theme-border)', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={async () => { const ok = await copyText(modalJson); setCopied(ok); setTimeout(() => setCopied(false), 1500); }}
                disabled={modalLoading}
                style={{ padding: '0.5rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, background: 'var(--theme-primary)', color: '#fff' }}>
                {copied ? 'Copied ✓' : 'Copy JSON'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const btnGhost = { padding: '0.5rem 0.9rem', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '0.82rem', whiteSpace: 'nowrap' };
const logTh = { padding: '0.5rem 0.6rem', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' };
const logTd = { padding: '0.45rem 0.6rem', color: '#1e293b' };

export default EventSyncLog;
